/**
 * React Query hook for the CRM dashboard.
 *
 * The dashboard query is heavy (8 parallel aggregations) and is also
 * cached server-side for 30 s. React Query keeps a per-tab cache so
 * repeated tab focus / route changes don't re-hit the backend.
 */

import { useQuery } from '@tanstack/react-query';
import api, { CrmDashboardMetrics } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function useDashboard(pipelineId?: string) {
  const query = useQuery<CrmDashboardMetrics>({
    queryKey: queryKeys.crm.dashboard(pipelineId),
    queryFn: () => api.getCrmDashboardMetrics(pipelineId),
    // Server caches 30 s; we mirror that on the client.
    staleTime: 30_000,
  });

  if (query.isError && !query.isFetching) {
    toast({ title: 'Error', description: 'Failed to load dashboard', variant: 'destructive' });
  }

  return {
    metrics: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}
